import apiClient from './client'
import type {
  BeginAuthRequest,
  BeginAuthResponse,
  SelectStrategyRequest,
  VerifyRequest,
  VerifyResponse,
  VerifyOTPResponse,
  User,
} from '@/types/auth'

const API_BASE = '/api/auth'

/**
 * Begin authentication session
 * Follows external dashboard auth guide format
 */
export async function beginAuth(
  identifier: string,
  operation: 'login' = 'login'
): Promise<{ success: boolean; data?: BeginAuthResponse; message?: string }> {
  try {
    const response = await apiClient.post<BeginAuthResponse>(
      `${API_BASE}/begin/`,
      {
        operation,
        identifier,
      } as BeginAuthRequest
    )

    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to start authentication',
    }
  }
}

/**
 * Select authentication strategy (if multiple strategies available)
 */
export async function selectStrategy(
  sessionId: string,
  strategy: string
): Promise<{ success: boolean; data?: BeginAuthResponse; message?: string }> {
  try {
    const payload = {
      session_id: sessionId,
      selected_strategy: strategy, // Backend expects 'selected_strategy' not 'strategy'
    }
    
    const response = await apiClient.post<BeginAuthResponse>(
      `${API_BASE}/select-strategy/`,
      payload
    )

    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to select strategy',
    }
  }
}

/**
 * Generate OTP challenge (send OTP to phone/email)
 * Follows external dashboard auth guide format
 */
export async function generateOTP(
  sessionId: string,
  identifier: string
): Promise<{ success: boolean; data?: VerifyResponse; message?: string }> {
  try {
    const payload = {
      session_id: sessionId,
      identifier,
      credential: identifier, // Use identifier (phone number) for phone OTP
      metadata: {
        action: 'generate_challenge',
      },
    }
    
    const response = await apiClient.post<VerifyResponse>(
      `${API_BASE}/verify/`,
      payload as VerifyRequest
    )

    // Check if response indicates an error even with 200 status
    if (response.data && (response.data as any).error) {
      const errorMsg = (response.data as any).error || (response.data as any).message || 'Failed to send OTP'
      return {
        success: false,
        message: errorMsg,
      }
    }
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to send OTP',
    }
  }
}

/**
 * Verify OTP and get tokens
 * Follows external dashboard auth guide format
 */
export async function verifyOTP(
  sessionId: string,
  identifier: string,
  otp: string,
  challengeId?: string
): Promise<{ success: boolean; data?: VerifyResponse; message?: string }> {
  try {
    const payload = {
      session_id: sessionId,
      identifier,
      credential: otp,
      metadata: {
        action: 'verify_otp', // Must be "verify_otp" for verification
        ...(challengeId && { challenge_id: challengeId }), // Include challenge_id if provided
      },
    }
    
    const response = await apiClient.post<VerifyResponse>(
      `${API_BASE}/verify/`,
      payload as VerifyRequest
    )

    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'OTP verification failed',
    }
  }
}

/**
 * Get current user profile
 */
export async function getMe(): Promise<{
  success: boolean
  data?: User
  message?: string
}> {
  try {
    const response = await apiClient.get<User>(`${API_BASE}/me/`)

    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch user profile',
    }
  }
}

/**
 * Logout (invalidate all refresh tokens)
 */
export async function logoutAll(): Promise<{
  success: boolean
  message?: string
}> {
  try {
    await apiClient.post(`${API_BASE}/logout-all/`, {
      confirm: true, // Backend requires confirm: true (boolean)
    })

    return {
      success: true,
    }
  } catch (error: any) {
    // Even if logout fails on server, we should clear local tokens
    return {
      success: true, // Return success to allow local cleanup
      message:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Logout completed locally',
    }
  }
}
